import { emit } from "."
import { useTelemetry } from "./telemetry-context"
import type { TelemetryValue } from "./telemetry-context"

/**
 * Shared telemetry-emission primitive for design system components (buttons,
 * modals, segmented controls, …).
 *
 * Resolves telemetry from context merged with the local `telemetry` prop and
 * returns an `emit`-like function. The component supplies the event name and a
 * default payload (e.g. a button's text, a modal's label); the resolved
 * telemetry is merged on top, so consumers can override any default key via the
 * `telemetry` prop. When telemetry resolves to `false`, the returned function
 * no-ops — nothing is emitted.
 *
 * Each component keeps ownership of *when* to emit (click, mount, value change)
 * and *what* the defaults are; only the resolve-merge-or-skip plumbing is
 * shared.
 *
 * `emit` is imported from the barrel (rather than called as a local binding)
 * so that `jest.spyOn(eventEmitter, "emit")` in consumers' tests intercepts it.
 */
export function useTelemetryEmitter(localTelemetry?: TelemetryValue) {
  const telemetryPayload = useTelemetry(localTelemetry)

  return (name: string, payload: Record<string, unknown> = {}): void => {
    if (telemetryPayload === false) return

    emit(name, { ...payload, ...telemetryPayload })
  }
}
